import { Badge } from "@dune/ui";
import {
	getBrokerName,
	getClientCompanyName,
	getClientFirstName,
	getClientLastName,
	getClientRef,
	getClientTypeLabel,
} from "@dune/utils";
import { ClientDetailsWithConstructionSites } from "@dune/vars";
import { ColumnDef } from "@tanstack/react-table";

const baseColumns: ColumnDef<ClientDetailsWithConstructionSites>[] = [
	{
		accessorKey: "ref",
		header: "Ref",
		cell: ({ row }) => getClientRef(row.original),
		enableSorting: true,
		sortingFn: (rowA, rowB) => {
			const refA = getClientRef(rowA.original);
			const refB = getClientRef(rowB.original);
			return refA.localeCompare(refB);
		},
	},
	{
		accessorKey: "lastName",
		header: "Nom",
		cell: ({ row }) => getClientLastName(row.original),
		enableSorting: true,
		sortingFn: (rowA, rowB) => {
			const nameA = getClientLastName(rowA.original);
			const nameB = getClientLastName(rowB.original);
			return nameA.localeCompare(nameB);
		},
	},
	{
		accessorKey: "firstName",
		header: "Prénom",
		cell: ({ row }) => getClientFirstName(row.original),
		enableSorting: true,
		sortingFn: (rowA, rowB) => {
			const firstnameA = getClientFirstName(rowA.original);
			const firstnameB = getClientFirstName(rowB.original);
			return firstnameA.localeCompare(firstnameB);
		},
	},
	{
		accessorKey: "companyName",
		header: "Raison Sociale",
		cell: ({ row }) => {
			const companyName = getClientCompanyName(row.original);
			return companyName ? (
				<div className="truncate max-w-[200px]" title={companyName}>
					{companyName}
				</div>
			) : (
				<span className="text-gray-400">—</span>
			);
		},
		enableSorting: true,
		sortingFn: (rowA, rowB) => {
			const companyA = getClientCompanyName(rowA.original);
			const companyB = getClientCompanyName(rowB.original);
			return companyA.localeCompare(companyB);
		},
	},
	{
		accessorKey: "type",
		header: "Type",
		cell: ({ row }) => getClientTypeLabel(row.original.clientType ?? ""),
		enableSorting: true,
		sortingFn: (rowA, rowB) => {
			const typeA = rowA.original.clientType ?? "";
			const typeB = rowB.original.clientType ?? "";
			return typeA.localeCompare(typeB);
		},
	},
	{
		accessorKey: "allConstructionSites",
		header: "Chantiers",
		cell: ({ row }) => {
			const client = row.original;
			const constructionSites = client.allConstructionSites || [];

			if (constructionSites.length === 0) {
				return <span className="text-gray-500">Aucun chantier</span>;
			}

			const maxDisplay = 3;
			const displaySites = constructionSites.slice(0, maxDisplay);
			const hasMore = constructionSites.length > maxDisplay;

			return (
				<div className="flex flex-wrap gap-1">
					{displaySites.map((site) => (
						<Badge key={site.id} variant="secondary" className="text-xs">
							{site.ref}
						</Badge>
					))}
					{hasMore && (
						<Badge variant="outline" className="text-xs">
							+{constructionSites.length - maxDisplay}
						</Badge>
					)}
				</div>
			);
		},
		enableSorting: false,
	},
];

const apporteursColumn: ColumnDef<ClientDetailsWithConstructionSites> = {
	accessorKey: "broker",
	header: "Apporteur",
	cell: ({ row }) => getBrokerName(row.original),
	enableSorting: true,
	sortingFn: (rowA, rowB) => {
		const brokerA = getBrokerName(rowA.original);
		const brokerB = getBrokerName(rowB.original);
		return brokerA.localeCompare(brokerB);
	},
};

export const getDuneColumns =
	(): ColumnDef<ClientDetailsWithConstructionSites>[] => {
		const columns = [...baseColumns];
		columns.splice(-1, 0, apporteursColumn);
		return columns;
	};

export const getBrokerColumns =
	(): ColumnDef<ClientDetailsWithConstructionSites>[] => {
		return baseColumns;
	};

export const clientColumns = baseColumns;
